import { createSelector } from "reselect";
import { RootState } from "redux/reducers/rootReducer";
import { getCountryCode, getUserLocation } from "./location";
import {
  getLocalizedDistanceAnyDispensary,
  getOpeningHours,
} from "./dispensaryHelper";

const getDispensary = (state: RootState) => state.dispensary;
export const getCurrentDispensary = createSelector(
  [getDispensary],
  (dispensary) => {
    return dispensary.currentDispensary;
  }
);

const getWithinDeliveryRange = createSelector(
  [getCurrentDispensary],
  (dispensary) => {
    const {
      deliveryZoneRangeType,
      deliveryZonePostalCodeWhitelist,
      deliveryRadiusMiles,
    } = dispensary || {};

    return deliveryZoneRangeType === "postal_code"
      ? !!deliveryZonePostalCodeWhitelist?.length
      : deliveryRadiusMiles && deliveryRadiusMiles > 0;
  }
);

export const getAcceptsOrders = createSelector(
  [getCurrentDispensary, getWithinDeliveryRange],
  (dispensary, withinDeliveryRange) => {
    const { hasReservationsEnabled, hasDeliveryEnabled } = dispensary || {};

    return (
      hasReservationsEnabled || (hasDeliveryEnabled && withinDeliveryRange)
    );
  }
);

export const getDispensaryUtm = createSelector(
  [getCurrentDispensary],
  (dispensary) => {
    const { website } = dispensary || {};
    if (!website) return null;

    return (
      website +
      "?utm_source=leafly.com&utm_medium=referral&utm_campaign=visit-website-link"
    );
  }
);

export const getLocalizedDistanceForCurrentDispensary = createSelector(
  [getCountryCode, getCurrentDispensary, getUserLocation],
  getLocalizedDistanceAnyDispensary
);

export const getIsDeliveryOnly = createSelector(
  [getCurrentDispensary],
  (dispensary) => {
    const tags = dispensary?.tags || [];
    return tags.includes("delivery") && !tags.includes("storefront");
  }
);

export const getPrimaryAddress = createSelector(
  [getCurrentDispensary],
  (dispensary): string => {
    if (!dispensary) {
      return "";
    }

    return [
      dispensary.address1,
      dispensary.address2,
      dispensary.city,
      dispensary.state,
    ]
      .filter((addressPart) => !!addressPart)
      .join(", ");
  }
);

export const getGoogleMapsUrl = createSelector(
  [getCurrentDispensary, getPrimaryAddress],
  (dispensary, primaryAddress) => {
    const { name, address1, address2 } = dispensary || {};

    if (address1 || address2) {
      const namePlusLocation = [name, primaryAddress]
        .join(" ")
        .replace(/\s/g, "+");
      return `https://maps.google.com/?q=${namePlusLocation}`;
    } else {
      return null;
    }
  }
);

export const getCurrentOpeningHours = createSelector(
  [getCurrentDispensary],
  (dispensary) => getOpeningHours(dispensary)
);

export const getIsUnclaimed = createSelector(
  [getCurrentDispensary],
  (dispensary) =>
    dispensary &&
    dispensary.featureTier === "info" &&
    dispensary.retailType === "clinic" &&
    dispensary.country === "US"
);

export const getRoundedRating = createSelector(
  [getCurrentDispensary],
  (dispensary) => {
    return dispensary?.roundedRating || 0;
  }
);

export const getIsDualMenu = createSelector(
  [getCurrentDispensary],
  (dispensary) => dispensary?.tags?.includes("dualLicense")
);

export const getHasFollowed = createSelector(
  [getDispensary],
  (dispensary) => dispensary.followed
);
